import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { safeNext } from "../router";
import type { PlanId } from "@coachbrain/contracts";
import type { AddClientInput } from "../api/types";
import { Card, Chip, PrimaryButton, SectionLabel, TapAnswers } from "@coachbrain/ui";
import { CoachShell } from "../components/common";
import { useApi } from "../lib/hooks";
import { en } from "../i18n/en";
import { termsDoc } from "../i18n/terms";

function TermsSheet({ onClose }: { onClose: () => void }) {
  return (
    <div className="cb-sheet" role="dialog" aria-modal="true" aria-label={termsDoc.title} onClick={onClose}>
      <div className="cb-sheet-panel" style={{ maxHeight: "80vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="cb-h3">{termsDoc.title}</div>
        <span className="cb-small cb-muted">{termsDoc.updated}</span>
        {termsDoc.sections.map((s) => <div key={s.h}><div className="cb-strong">{s.h}</div><p className="cb-small" style={{ margin: "4px 0 0" }}>{s.body}</p></div>)}
        <button type="button" className="cb-btn cb-btn-outline" onClick={onClose}>{en.common.close}</button>
      </div>
    </div>
  );
}

/** Whether the API offers Google sign-in; false while loading and in mock mode. */
function useGoogleSignIn(): boolean {
  const api = useApi();
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    void api?.authConfig().then((c) => { if (live) setEnabled(c.google); }).catch(() => {});
    return () => { live = false; };
  }, [api]);
  return enabled;
}

/** Full navigation, not fetch: the API answers with a redirect to Google's consent screen. */
function GoogleButton({ next }: { next: string }) {
  return (
    <a className="cb-btn cb-btn-outline cb-btn-google" href={`/api/auth/google?next=${encodeURIComponent(next)}`}>
      <GoogleMark />
      <span>{en.auth.continueWithGoogle}</span>
    </a>
  );
}
/** Google's "G" mark in its brand colours, as the sign-in button guidelines ask. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.3v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8z" />
    </svg>
  );
}

export function LoginRoute() {
  const api = useApi(); const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { next?: string; error?: string };
  const next = safeNext(search.next);
  const google = useGoogleSignIn();
  const [email, setEmail] = useState("dani@example.com"); const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  return (
    <CoachShell title={en.app} subtitle="A thinking partner for coaches" hideNav narrow>
      <Card>
        <form onSubmit={async (e) => { e.preventDefault(); try { await api!.login({ email, password }); void navigate({ to: next as "/" }); } catch (x) { setErr(x instanceof Error ? x.message : String(x)); } }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="cb-field"><label htmlFor="email">{en.auth.email}</label><input id="email" className="cb-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="cb-field"><label htmlFor="pw">{en.auth.password}</label><input id="pw" className="cb-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          {search.error?.startsWith("google") ? <span className="cb-error">{en.auth.googleError}</span> : null}
          {err ? <span className="cb-error">{err}</span> : null}
          <PrimaryButton type="submit" tone="navy" full disabled={!api}>{en.auth.login}</PrimaryButton>
          {google ? <GoogleButton next={next} /> : null}
        </form>
        <p className="cb-small cb-muted">{en.auth.or} <button type="button" className="cb-link-button" onClick={() => void navigate({ to: "/signup" })}>{en.auth.signup}</button></p>
      </Card>
    </CoachShell>
  );
}

export function SignupRoute() {
  const api = useApi(); const navigate = useNavigate();
  // A marketing CTA arrives with ?plan=<planId> (validated by the route); signup keeps it and lands on the settings picker.
  const { plan } = useSearch({ strict: false }) as { plan?: PlanId };
  const google = useGoogleSignIn();
  const [f, setF] = useState({ name: "", email: "", password: "", country: "PT", practiceName: "", acceptTerms: false });
  const [err, setErr] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF((s) => ({ ...s, [k]: v }));
  return (
    <CoachShell title={en.auth.signup} subtitle={en.auth.freePeriod} hideNav narrow>
      <Card>
        <form onSubmit={async (e) => { e.preventDefault(); if (!f.acceptTerms) return; try { await api!.signup({ ...f, acceptTerms: true, practiceName: f.practiceName || undefined }); void navigate(plan ? { to: "/settings", search: { plan } } : { to: "/onboarding" }); } catch (x) { setErr(x instanceof Error ? x.message : String(x)); } }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="cb-field"><label htmlFor="n">{en.auth.name}</label><input id="n" className="cb-input" value={f.name} onChange={(e) => set("name", e.target.value)} required autoComplete="name" /></div>
          <div className="cb-field"><label htmlFor="e">{en.auth.email}</label><input id="e" className="cb-input" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required autoComplete="email" /></div>
          <div className="cb-field"><label htmlFor="p">{en.auth.password}</label><input id="p" className="cb-input" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required minLength={10} autoComplete="new-password" /></div>
          <div className="cb-field"><label htmlFor="c">{en.auth.country}</label><select id="c" className="cb-select" value={f.country} onChange={(e) => set("country", e.target.value)}>{["PT", "ES", "GB", "US", "DE", "FR", "IE", "NL"].map((c) => <option key={c}>{c}</option>)}</select></div>
          <div className="cb-field"><label htmlFor="pn">{en.auth.practiceName}</label><input id="pn" className="cb-input" value={f.practiceName} placeholder={f.name || "Defaults to your name"} onChange={(e) => set("practiceName", e.target.value)} /></div>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={f.acceptTerms} onChange={(e) => set("acceptTerms", e.target.checked)} required /> <span>{en.auth.terms} <button type="button" className="cb-link-button" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>{en.auth.termsLink}</button></span></label>
          {err ? <span className="cb-error">{err}</span> : null}
          <PrimaryButton type="submit" tone="emerald" full disabled={!api}>{en.auth.signup}</PrimaryButton>
          {google ? <GoogleButton next="/onboarding" /> : null}
        </form>
      </Card>
      {showTerms ? <TermsSheet onClose={() => setShowTerms(false)} /> : null}
    </CoachShell>
  );
}

/** Lands here from the API callback with ?ticket= when Google authenticated someone we don't know yet. */
export function GoogleFinishRoute() {
  const api = useApi(); const navigate = useNavigate();
  const { ticket } = useSearch({ strict: false }) as { ticket?: string };
  const [pending, setPending] = useState<{ email: string; name: string } | null>(null);
  const [invalid, setInvalid] = useState(!ticket);
  const [f, setF] = useState({ country: "PT", practiceName: "", acceptTerms: false });
  const [err, setErr] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    if (!api || !ticket) return;
    let live = true;
    api.googlePending(ticket).then((p) => { if (live) setPending(p); }).catch(() => { if (live) setInvalid(true); });
    return () => { live = false; };
  }, [api, ticket]);
  return (
    <CoachShell title={en.auth.googleFinishTitle} subtitle={pending ? en.auth.googleFinishAs(pending.email) : en.auth.freePeriod} hideNav narrow>
      <Card>
        {invalid ? (
          <p>{en.auth.googleTicketInvalid} <button type="button" className="cb-link-button" onClick={() => void navigate({ to: "/signup" })}>{en.auth.signup}</button></p>
        ) : (
          <form onSubmit={async (e) => { e.preventDefault(); if (!f.acceptTerms || !ticket) return; try { await api!.googleComplete({ ticket, country: f.country, practiceName: f.practiceName || undefined, acceptTerms: true }); void navigate({ to: "/onboarding" }); } catch (x) { setErr(x instanceof Error ? x.message : String(x)); } }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="cb-field"><label htmlFor="gc">{en.auth.country}</label><select id="gc" className="cb-select" value={f.country} onChange={(e) => set("country", e.target.value)}>{["PT", "ES", "GB", "US", "DE", "FR", "IE", "NL"].map((c) => <option key={c}>{c}</option>)}</select></div>
            <div className="cb-field"><label htmlFor="gpn">{en.auth.practiceName}</label><input id="gpn" className="cb-input" value={f.practiceName} placeholder={pending?.name || "Defaults to your name"} onChange={(e) => set("practiceName", e.target.value)} /></div>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={f.acceptTerms} onChange={(e) => set("acceptTerms", e.target.checked)} required /> <span>{en.auth.terms} <button type="button" className="cb-link-button" onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>{en.auth.termsLink}</button></span></label>
            {err ? <span className="cb-error">{err}</span> : null}
            <PrimaryButton type="submit" tone="emerald" full disabled={!api}>{en.auth.signup}</PrimaryButton>
          </form>
        )}
      </Card>
      {showTerms ? <TermsSheet onClose={() => setShowTerms(false)} /> : null}
    </CoachShell>
  );
}

export function OnboardingRoute() {
  const api = useApi(); const navigate = useNavigate();
  const t = en.onboarding;
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [channel, setChannel] = useState<AddClientInput["channel"]>({ kind: "email", address: "", consentText: t.consentDefault });
  const [injury, setInjury] = useState<AddClientInput["injury"] | null>(null);
  const [injuryLoc, setInjuryLoc] = useState("");
  const [goal, setGoal] = useState<AddClientInput["goal"]>({ verbatim: "", goalType: "performance", horizon: "long" });
  const [strategy, setStrategy] = useState("");
  const [busy, setBusy] = useState(false);
  const next = () => setStep((s) => Math.min(3, s + 1));
  const finish = async (sample = false) => {
    if (!api) return; setBusy(true);
    try {
      const c = sample ? await api.addClient({ name: "Fred Okonkwo", channel: { kind: "email", address: "fred@example.com", consentText: t.consentDefault }, goal: { verbatim: "Run a 5k without stopping", goalType: "performance", horizon: "long" }, injury: { none: false, reports: [{ location: "right knee", kind: "pain" }] }, sample: true })
        : await api.addClient({ name, channel, goal, injury: injury ?? { none: true }, strategy: strategy ? { intent: strategy, principles: [] } : undefined });
      void navigate({ to: `/clients/${c.id}` });
    } finally { setBusy(false); }
  };
  return (
    <CoachShell title={t.title} subtitle={`${step + 1} / 4 · ${t.steps[step]}`} hideNav narrow>
      <Card>
        {step === 0 ? <><div className="cb-field"><label htmlFor="cn">{t.name}</label><input id="cn" className="cb-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div><PrimaryButton tone="navy" full disabled={!name.trim()} onClick={next}>Next</PrimaryButton><button type="button" className="cb-link-button" onClick={() => void finish(true)}>{t.sample}</button></> : null}
        {step === 1 ? <>
          <SectionLabel>{t.channel}</SectionLabel>
          <div className="cb-inline-actions">{(["email", "sms"] as const).map((k) => <Chip key={k} tone={channel.kind === k ? "navy" : "ground"} onClick={() => setChannel((c) => ({ ...c, kind: k }))}>{k}</Chip>)}</div>
          <div className="cb-field"><label htmlFor="addr">{channel.kind === "email" ? "Email address" : "Mobile number"}</label><input id="addr" className="cb-input" type={channel.kind === "email" ? "email" : "tel"} value={channel.address} onChange={(e) => setChannel((c) => ({ ...c, address: e.target.value }))} /></div>
          <div className="cb-field"><label htmlFor="consent">{t.consent}</label><textarea id="consent" className="cb-textarea" value={channel.consentText} onChange={(e) => setChannel((c) => ({ ...c, consentText: e.target.value }))} /></div>
          <PrimaryButton tone="navy" full disabled={!channel.address.trim()} onClick={next}>Client consented — next</PrimaryButton>
        </> : null}
        {step === 2 ? <>
          <div className="cb-h3">{t.injury}</div>
          <TapAnswers options={[t.none, t.some]} picked={injury ? (injury.none ? t.none : t.some) : null} primaryFirst={false} onPick={(o) => setInjury(o === t.none ? { none: true } : { none: false, reports: [{ location: injuryLoc || "unspecified", kind: "unclassified" }] })} />
          {injury && !injury.none ? <div className="cb-field"><label htmlFor="loc">{t.location}</label><input id="loc" className="cb-input" value={injuryLoc} onChange={(e) => { setInjuryLoc(e.target.value); setInjury({ none: false, reports: [{ location: e.target.value, kind: "unclassified" }] }); }} /></div> : null}
          <PrimaryButton tone="navy" full disabled={!injury} onClick={next}>Next</PrimaryButton>
        </> : null}
        {step === 3 ? <>
          <div className="cb-field"><label htmlFor="gv">{t.goalVerbatim}</label><input id="gv" className="cb-input" placeholder="“Run a 5k without stopping”" value={goal.verbatim} onChange={(e) => setGoal((g) => ({ ...g, verbatim: e.target.value }))} /></div>
          <div className="cb-grid-2">
            <div className="cb-field"><label htmlFor="gt">{t.goalType}</label><select id="gt" className="cb-select" value={goal.goalType} onChange={(e) => setGoal((g) => ({ ...g, goalType: e.target.value as typeof g.goalType }))}>{["outcome", "performance", "health", "behavior"].map((x) => <option key={x}>{x}</option>)}</select></div>
            <div className="cb-field"><label htmlFor="gh">{t.horizon}</label><select id="gh" className="cb-select" value={goal.horizon} onChange={(e) => setGoal((g) => ({ ...g, horizon: e.target.value as typeof g.horizon }))}><option value="long">long (&gt; 12 weeks)</option><option value="short">short (≤ 12 weeks)</option></select></div>
          </div>
          <div className="cb-field"><label htmlFor="st">{t.strategy}</label><textarea id="st" className="cb-textarea" style={{ minHeight: 70 }} value={strategy} onChange={(e) => setStrategy(e.target.value)} /></div>
          <PrimaryButton tone="emerald" full disabled={!goal.verbatim.trim() || busy} onClick={() => void finish()}>{t.finish}</PrimaryButton>
        </> : null}
      </Card>
    </CoachShell>
  );
}
